import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../common/axiosInstans.ts';
import {
    Button,
    Switch,
    Typography,
    Box,
    Divider,
    Alert,
    IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Loader from '../../common/components/Loader.tsx';
import { useGlobalStore } from '../../application/store/store.tsx';
import { Drug, DrugSynonym } from '../../application/types/drug.ts';
import DrugSynonymService from './services/DrugSynonymService.ts';
import SynonymList from './components/SynonymList.tsx';
import SynonymDialog from './components/SynonymDialog.tsx';
import DeleteConfirmDialog from './components/DeleteConfirmDialog.tsx';
import EditDrugIdentifiersDialog from './components/EditDrugIdentifiersDialog.tsx';
import ConfirmDialog from './components/ConfirmDialog.tsx';

const DrugView = () => {
    const { id } = useParams();
    const [drug, setDrug] = useState<Drug | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [synonymError, setSynonymError] = useState<string | null>(null);
    const [synonymDialogOpen, setSynonymDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingSynonym, setEditingSynonym] = useState<DrugSynonym | null>(
        null
    );
    const [deletingSynonym, setDeletingSynonym] = useState<DrugSynonym | null>(
        null
    );
    const [isSynonymLoading, setIsSynonymLoading] = useState(false);
    const [editIdentifiersDialogOpen, setEditIdentifiersDialogOpen] =
        useState(false);
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
    const [isStatusChanging, setIsStatusChanging] = useState(false);
    const setMessage = useGlobalStore((state) => state.setMessage);
    const navigate = useNavigate();
    useEffect(() => {
        const fetchDrug = async () => {
            try {
                const response = await axiosInstance.get(`/drug/${id}`);
                setDrug(response.data);
            } catch (error) {
                setError('Error fetching drug data. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchDrug();
    }, [id]);

    const handleStatusToggle = () => {
        if (drug?.isActive) {
            setDeactivateDialogOpen(true);
        } else {
            setReactivateDialogOpen(true);
        }
    };

    const deactivateDrug = async () => {
        try {
            setIsStatusChanging(true);
            await axiosInstance.delete(`/drug/${id}`);
            setDrug((prevDrug) => ({
                ...prevDrug!,
                isActive: false,
            }));
            setMessage('Läkemedel inaktiverat', 'success');
            setDeactivateDialogOpen(false);
        } catch (error) {
            setMessage('Ett fel uppstod', 'error', 10000);
            console.error('Error deactivating drug:', error);
        } finally {
            setIsStatusChanging(false);
        }
    };

    const reactivateDrug = async () => {
        try {
            setIsStatusChanging(true);
            await axiosInstance.put(`/drug/reactivate/${id}`);
            setDrug((prevDrug) => ({
                ...prevDrug!,
                isActive: true,
            }));
            setMessage('Läkemedel återaktiverat', 'success');
            setReactivateDialogOpen(false);
        } catch (error) {
            setMessage('Ett fel uppstod', 'error', 10000);
            console.error('Error reactivating drug:', error);
        } finally {
            setIsStatusChanging(false);
        }
    };

    const toggleAllowSynonyms = async () => {
        if (!drug || !id) return;

        const newValue = !drug.allowSynonyms;
        setSynonymError(null);

        try {
            setIsSynonymLoading(true);
            await DrugSynonymService.setAllowSynonyms(id, newValue);
            setDrug((prevDrug) => ({
                ...prevDrug!,
                allowSynonyms: newValue,
            }));
            setMessage(
                newValue ? 'Synonymer aktiverade' : 'Synonymer inaktiverade',
                'success'
            );
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data ||
                'Ett fel uppstod';
            setSynonymError(errorMessage);
        } finally {
            setIsSynonymLoading(false);
        }
    };

    const handleAddSynonym = () => {
        setEditingSynonym(null);
        setSynonymDialogOpen(true);
    };

    const handleEditSynonym = (synonym: DrugSynonym) => {
        setEditingSynonym(synonym);
        setSynonymDialogOpen(true);
    };

    const handleDeleteSynonym = (synonym: DrugSynonym) => {
        setDeletingSynonym(synonym);
        setDeleteDialogOpen(true);
    };

    const handleSaveSynonym = async (data: {
        barCode?: string;
        gtin?: string;
        produktName?: string;
    }) => {
        if (!id) return;

        setIsSynonymLoading(true);
        try {
            if (editingSynonym) {
                // Update existing synonym
                const updated = await DrugSynonymService.updateSynonym(
                    editingSynonym.id,
                    data
                );
                setDrug((prevDrug) => ({
                    ...prevDrug!,
                    synonyms: prevDrug?.synonyms?.map((s) =>
                        s.id === updated.id ? updated : s
                    ),
                }));
                setMessage('Synonym uppdaterad', 'success');
            } else {
                // Create new synonym
                const created = await DrugSynonymService.createSynonym({
                    drugId: id,
                    ...data,
                });
                setDrug((prevDrug) => ({
                    ...prevDrug!,
                    synonyms: [...(prevDrug?.synonyms || []), created],
                }));
                setMessage('Synonym tillagd', 'success');
            }
            setSynonymDialogOpen(false);
        } catch (error: any) {
            throw error;
        } finally {
            setIsSynonymLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deletingSynonym) return;

        setIsSynonymLoading(true);
        try {
            await DrugSynonymService.deleteSynonym(deletingSynonym.id);
            setDrug((prevDrug) => ({
                ...prevDrug!,
                synonyms: prevDrug?.synonyms?.filter(
                    (s) => s.id !== deletingSynonym.id
                ),
            }));
            setMessage('Synonym borttagen', 'success');
            setDeleteDialogOpen(false);
            setDeletingSynonym(null);
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data ||
                'Ett fel uppstod';
            setMessage(errorMessage, 'error');
        } finally {
            setIsSynonymLoading(false);
        }
    };

    const handleSaveIdentifiers = async (data: {
        atc?: string;
        barCode?: string;
        gtin?: string;
    }) => {
        if (!id || !drug) return;

        try {
            await axiosInstance.put(`/drug/${id}`, {
                id,
                atc: data.atc,
                barCode: data.barCode,
                gtin: data.gtin,
            });
            setDrug((prevDrug) => ({
                ...prevDrug!,
                atc: data.atc || null,
                barCode: data.barCode || null,
                gtin: data.gtin || null,
            }));
            setMessage('Läkemedel uppdaterat', 'success');
        } catch (error: any) {
            throw error;
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    return (
        <div className="px-4 py-2">
            <div className="max-w-2xl mx-auto">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('..')}
                    color="primary"
                    className="mb-4"
                >
                    Tillbaka
                </Button>

                <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
                    <h1 className="text-2xl sm:text-3xl font-bold mb-4 break-words">
                        {drug.drugName}
                    </h1>

                    <div className="mb-4 text-sm sm:text-base">
                        <strong>Koncentration:</strong> {drug.concentration}{' '}
                        {drug.concentrationUnit}
                    </div>

                    <div className="mb-4 text-sm sm:text-base">
                        <strong>Mängd:</strong> {drug.quantity}{' '}
                        {drug.quantityUnit}
                    </div>

                    <div className="mb-4 text-sm sm:text-base">
                        <strong>Dosform:</strong> {drug.dosageForm}
                    </div>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            Identifierare
                        </Typography>
                        {drug?.isActive && (
                            <IconButton
                                size="small"
                                onClick={() =>
                                    setEditIdentifiersDialogOpen(true)
                                }
                                title="Redigera identifierare"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>

                    <div className="mb-4 text-sm sm:text-base">
                        <strong>ATC:</strong> {drug.atc || '-'}
                    </div>

                    <div className="mb-4 text-sm sm:text-base">
                        <strong>Streckkod:</strong> {drug.barCode || '-'}
                    </div>

                    <div className="mb-4 text-sm sm:text-base">
                        <strong>GTIN:</strong> {drug.gtin || '-'}
                    </div>

                    <Divider sx={{ my: 2 }} />

                    <div className="mb-4 flex items-center gap-2 text-sm sm:text-base">
                        <strong>Status:</strong>
                        <span>{drug?.isActive ? 'Aktiv' : 'Inaktiv'}</span>
                        <Switch
                            onChange={handleStatusToggle}
                            checked={drug?.isActive}
                            disabled={isStatusChanging}
                        />
                    </div>

                    {error && (
                        <p className="text-red-500 text-sm mt-2">{error}</p>
                    )}
                </div>

                {/* Synonyms Section - only shown for active drugs */}
                {drug?.isActive && (
                    <Box
                        sx={{ mt: 3 }}
                        className="bg-white shadow-md rounded-lg p-4 sm:p-6"
                    >
                        <Typography variant="h6" gutterBottom>
                            Synonympreparat
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Alternativa streckkoder/GTIN som pekar på detta
                            läkemedel
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <Typography>Tillåt synonymer:</Typography>
                            <Switch
                                checked={drug?.allowSynonyms || false}
                                onChange={toggleAllowSynonyms}
                                disabled={isSynonymLoading}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {drug?.allowSynonyms
                                    ? 'Aktiverat'
                                    : 'Inaktiverat'}
                            </Typography>
                        </Box>

                        {synonymError && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setSynonymError(null)}
                            >
                                {synonymError}
                            </Alert>
                        )}

                        {drug?.allowSynonyms && (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddSynonym}
                                        disabled={isSynonymLoading}
                                    >
                                        Lägg till synonym
                                    </Button>
                                </Box>

                                <SynonymList
                                    synonyms={drug?.synonyms || []}
                                    onEdit={handleEditSynonym}
                                    onDelete={handleDeleteSynonym}
                                    disabled={isSynonymLoading}
                                />
                            </>
                        )}
                    </Box>
                )}
            </div>

            {/* Synonym Dialog */}
            <SynonymDialog
                open={synonymDialogOpen}
                onClose={() => setSynonymDialogOpen(false)}
                onSave={handleSaveSynonym}
                synonym={editingSynonym}
                isLoading={isSynonymLoading}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setDeletingSynonym(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Ta bort synonym"
                message={`Är du säker på att du vill ta bort synonymen "${deletingSynonym?.produktName || deletingSynonym?.barCode || deletingSynonym?.gtin}"?`}
                isLoading={isSynonymLoading}
            />

            {/* Edit Drug Identifiers Dialog */}
            <EditDrugIdentifiersDialog
                open={editIdentifiersDialogOpen}
                onClose={() => setEditIdentifiersDialogOpen(false)}
                onSave={handleSaveIdentifiers}
                drug={drug}
            />

            {/* Deactivate Confirmation Dialog */}
            <ConfirmDialog
                open={deactivateDialogOpen}
                onClose={() => setDeactivateDialogOpen(false)}
                onConfirm={deactivateDrug}
                title="Inaktivera läkemedel"
                message={`Är du säker på att du vill inaktivera "${drug?.drugName}"? Läkemedlet kommer inte längre kunna användas i transaktioner.`}
                confirmText="Inaktivera"
                confirmingText="Inaktiverar..."
                confirmColor="error"
                isLoading={isStatusChanging}
            />

            {/* Reactivate Confirmation Dialog */}
            <ConfirmDialog
                open={reactivateDialogOpen}
                onClose={() => setReactivateDialogOpen(false)}
                onConfirm={reactivateDrug}
                title="Återaktivera läkemedel"
                message={`Är du säker på att du vill återaktivera "${drug?.drugName}"?`}
                confirmText="Återaktivera"
                confirmingText="Återaktiverar..."
                confirmColor="success"
                isLoading={isStatusChanging}
            />
        </div>
    );
};

export default DrugView;
